'use strict';

/**
 * @module List
 * @author Philip & Micha
 */

const $ = require('zepto');
const PlaceView = require('./place');
const CityView = require('./city');
const GeoSearchView = require('./geosearch');

const DataHelper = require('../helpers/data');
const ConfigurationHelper = require('../helpers/configuration');
const EventHelper = require('../helpers/event');

const TOPCITY_THRESHOLD = ConfigurationHelper.getTopCityThreshold();

class List {
	constructor () {
		this.places = {};
		this.cities = {};
		this.allCities = [];

		this.$el = $('.list'); // needed for templateHelper stuff
		
		// GeoSearchView
		this.$geosearch = $('<div class="list__geosearch" />');
		this.$el.append(this.$geosearch);
		new GeoSearchView(Object.assign({}, this, {$el : this.$geosearch})).render();
		
		// no entries
		this.$nocitynoplace = $('<div class="list__noresult list__noresult--hidden noresult">' +
			'<p class="noresult__text">' + ConfigurationHelper.getString('list.noresult.text') + '</p>' +
			'<h3 class="noresult__headline">' + ConfigurationHelper.getString('list.noresult.headline') + '</h3>' +
		'</div>');
		this.$el.append(this.$nocitynoplace);

		// we want to have separated big cities
		this.$toplist = $('<ul class="list__group list__group--top" />');
		this.$el.append(this.$toplist);
		// this.$toplist = this.$el.find('.list__group--top');

		this.$defaultlist = $('<ul class="list__group list__group--default" />');
		this.$el.append(this.$defaultlist);
	}
	
	update(markers, bounds) {
		// get center of bounds
		const centerLat = bounds.getCenter().lat();
		const centerLng = bounds.getCenter().lng();

		/* ***************** Places ****************** */
		
		
		// filter places if visible
		let placesFiltered = markers.places.filter(place => {
			return bounds.contains(new google.maps.LatLng(place.lat, place.lng));
		});
		
		// set order and selected
		placesFiltered.forEach(place => {
			place.distance = this.calculateDistance(centerLat, centerLng, place.lat, place.lng);
			place.selected = EventHelper.isPlaceSelected(place.id);
		});
		
		// sort by order for limiting the correct places in next step
		placesFiltered.sort((a,b) => (a.distance > b.distance) ? 1 : ((b.distance > a.distance) ? -1 : 0));
		
		// limit places in list to maxPlacesInList
		placesFiltered = placesFiltered.slice(0, ConfigurationHelper.getMaxPlacesInList());
		
		// add or update places
		placesFiltered.forEach(async place => {
			if(!this.places[place.id]) {
				let placeFull = await DataHelper.request('getPlaceById', {placeId: place.id});
				// add attribuates back to placeFull
				placeFull = Object.assign(place, placeFull);
				const p = new PlaceView(Object.assign({}, this, {$el : this.$defaultlist}), placeFull);
				this.places[place.id] = p;
				p.render();
			} else {
				// place already added, so update its new attributes
				this.places[place.id].updateDistance(place.distance);
				this.places[place.id].selected(place.selected);
			}
		});
		
		// remove places
		Object.entries(this.places).forEach(([id, view]) => {
			if(placesFiltered.findIndex(c => c.id === id) === -1) {
				view.remove();
				delete this.places[id];
			}
		});
		
		
		/* ***************** Cities ****************** */
		
		// filter cities if visible
		const citiesFiltered = markers.cities.filter(city => {
			return bounds.contains(new google.maps.LatLng(city.lat, city.lng));
		})
		
		// set order of cities
		citiesFiltered.sort((a, b) => {
			return a.name.localeCompare(b.name, ConfigurationHelper.getCurrentLanguage());
		});
		citiesFiltered.sort(this.sortTopCities);

		// add or update cities
		citiesFiltered.forEach(city => {
			this.renderCity(city);
		});

		// remove cities
		Object.entries(this.cities).forEach(([id, view]) => {
			if(citiesFiltered.findIndex(c => c.id === id) === -1) {
				view.remove();
				delete this.cities[id];
			}
		});

		// anything visible?
		const noresults = (citiesFiltered.length === 0 && placesFiltered.length === 0);
		this.$nocitynoplace.toggleClass('list__noresult--hidden', !noresults);
		if (noresults) {
			(async () => {
				if (this.allCities.length === 0) {
					this.allCities = await DataHelper.request('getCities');
				}
				// render all cities
				this.allCities.forEach(city => {
					this.renderCity(city);
				});
			})();
		}
	}
	
	renderCity(city) {
		if(!this.cities[city.id]) {
			let c;
			let $list;
			if (city.placeCount > TOPCITY_THRESHOLD) {
				$list = this.$toplist;
			} else {
				$list = this.$defaultlist;
			}
			c = new CityView(Object.assign({}, this, {$el : $list}), city);
			this.cities[city.id] = c;
			c.render();
		} else {
			// city already added, so update its order
			this.cities[city.id].updateOrder(city.order);
		}
	}
	
	calculateDistance(lat1, lon1, lat2, lon2) { // https://stackoverflow.com/a/26837460
		const radlat1 = Math.PI * lat1/180;
		const radlat2 = Math.PI * lat2/180;
		const theta = lon1 - lon2;
		const radtheta = Math.PI * theta/180;
		let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
		dist = Math.acos(dist);
		dist = dist * 180/Math.PI;
		dist = dist * 60 * 1.1515;
		dist = dist * 1.609344; // dist is now km
		dist = Math.floor(dist * 1000);
		return dist
	}

	sortTopCities(a, b) {
		if (a.placeCount > TOPCITY_THRESHOLD) {
			return b.placeCount - a.placeCount;
		}
	}
}

module.exports = List;
